import { BeforeMount, Editor, OnMount } from "@monaco-editor/react";
import { Box, Typography } from "@mui/material";
import SysColumn from "app/class/sys-column";
import { usePlatformTheme } from "app/providers/PlatformThemeProvider";
import type * as monacoType from "monaco-editor";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyMonacoPlatformTheme } from "modules/record/record-producer/components/record-producer-fields/code-editor/applyMonacoPlatformTheme";
import { configureCodeEditor } from "modules/record/record-producer/components/record-producer-fields/code-editor/configureCodeEditor";
import { getCodeEditorProfile } from "modules/record/record-producer/components/record-producer-fields/code-editor/profiles";
import { CodeEditorProfile } from "modules/record/record-producer/components/record-producer-fields/code-editor/types";
import { BuilderFieldDef } from "../../types/formBuilder.types";

interface ScriptEditorProps {
    value: string;
    fields: BuilderFieldDef[];
    onChange: (value: string) => void;
}

const SCRIPT_EDITOR_PATH = "file:///primum/service-portal/form-builder/script-code.js";

const fieldTypeToColumnType = (field: BuilderFieldDef) => {
    switch (field.type) {
        case "textarea":
        case "richtext":
        case "password":
        case "email":
        case "url":
        case "color":
            return "TEXT";
        case "number":
            return "INTEGER";
        case "decimal":
            return "DECIMAL";
        case "currency":
            return "CURRENCY";
        case "boolean":
            return "BOOLEAN";
        case "date":
            return "DATE";
        case "datetime":
            return "DATETIME";
        case "time":
            return "TIME";
        case "choice":
            return "CHOICE";
        case "reference":
            return "REFERENCE";
        case "attachment":
            return "ATTACHMENT";
        case "text":
        default:
            return "TEXT";
    }
};

const buildFieldColumns = (fields: BuilderFieldDef[]) =>
    fields
        .filter((field) => field.name)
        .map((field, index) => {
            const column = new SysColumn();

            column.id = field.name;
            column.name = field.name;
            // PRIM-004 (rodada 4): intencionalmente NÃO traduzido. `column` aqui vira o
            // `targetColumns` do CodeEditorProfile — dado consumido pelo autocomplete do Monaco
            // dentro do script de usuário (JS), não texto de UI. Se `field.label` for uma key de
            // tradução, é isso que deve aparecer na sugestão/autocomplete: o script escrito pelo
            // desenvolvedor quer o valor canônico do metadado, não o texto localizado no idioma
            // de quem está editando o script naquele momento.
            column.label = field.label || field.name;
            column.active = true;
            column.order = index;
            column.column_type = fieldTypeToColumnType(field);
            column.column_subtype = field.type;
            column.referenced_table_name = field.source || "";
            column.reference_filter = field.referenceFilter ? JSON.stringify(field.referenceFilter) : "";
            column.default_value = field.defaultValue || "";
            column.is_mandatory = Boolean(field.required);

            return column;
        });

const buildScriptProfile = (fields: BuilderFieldDef[]): CodeEditorProfile => ({
    ...getCodeEditorProfile("service_portal.form_script"),
    targetColumns: buildFieldColumns(fields),
});

const ScriptEditor = ({ value, fields, onChange }: ScriptEditorProps) => {
    const { t } = useTranslation();
    const { activeTheme } = usePlatformTheme();
    const [monacoInstance, setMonacoInstance] = useState<typeof monacoType | null>(null);
    const profile = useMemo(() => buildScriptProfile(fields), [fields]);

    const beforeMount: BeforeMount = useCallback((monaco) => {
        applyMonacoPlatformTheme(monaco, activeTheme);
    }, [activeTheme]);

    const handleMount: OnMount = useCallback((_editor, monaco) => {
        setMonacoInstance(monaco);
    }, []);

    useEffect(() => {
        if (!monacoInstance) {
            return;
        }

        applyMonacoPlatformTheme(monacoInstance, activeTheme);
    }, [activeTheme, monacoInstance]);

    useEffect(() => {
        if (!monacoInstance) {
            return undefined;
        }

        const disposable = configureCodeEditor(monacoInstance, profile, SCRIPT_EDITOR_PATH, (key, fallback) =>
            t(key, { defaultValue: fallback || "" })
        );

        return () => disposable.dispose();
    }, [monacoInstance, profile, t]);

    return (
        <Box className="service-builder__developer-editor">
            <Box className="service-builder__script-hint">
                <Typography variant="body2">{t("Gate runtime autocomplete is enabled for form scripts and handlers.")}</Typography>
            </Box>
            <Box className="service-builder__monaco-frame">
                <Editor
                    height="100%"
                    language="javascript"
                    path={SCRIPT_EDITOR_PATH}
                    theme="primum-platform-editor"
                    beforeMount={beforeMount}
                    onMount={handleMount}
                    value={value}
                    onChange={(nextValue) => onChange(nextValue || "")}
                    options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        automaticLayout: true,
                        scrollBeyondLastLine: false,
                        wordWrap: "on",
                    }}
                />
            </Box>
        </Box>
    );
};

export default ScriptEditor;
